import Seo from "@/components/Seo";
import React from "react";
import { Link } from "react-router-dom";
import PageMasthead from "@/components/PageMasthead";
import RichText from "@/components/RichText";
import { FadeUp } from "@/components/motion";
import { MapPin, Briefcase, ArrowRight } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import useApi from "@/hooks/useApi";

const perks = [
  { title: "Independence with impact", body: "Do the most meaningful work of your career on brands that genuinely change families' lives." },
  { title: "A newsroom that ships", body: "Fast-moving, data-driven and ambitious — with the reach of six brands behind you." },
  { title: "Grow across the group", body: "From editorial to data to commercial, careers here scale across markets and continents." },
];

const Careers = () => {
  const { data } = useApi("/jobs");
  const list = data || [];
  const jobLd = list.filter((j) => !j.closed).map((j) => ({
    "@context": "https://schema.org",
    "@type": "JobPosting",
    title: j.title,
    description: j.description || j.summary || "",
    datePosted: j.created_at || j.date,
    employmentType: j.type || "FULL_TIME",
    hiringOrganization: { "@type": "Organization", name: "Which Media", sameAs: "https://which-media.com" },
    jobLocation: {
      "@type": "Place",
      address: { "@type": "PostalAddress", addressLocality: j.location || "Dubai", addressCountry: j.country || "AE" },
    },
    directApply: true,
  }));
  return (
    <div data-testid="careers-page">
      <Seo
        title="Careers"
        path="/careers"
        description="Join Which Media — we hire journalists, analysts, product and commercial talent who care deeply about getting education intelligence right."
        jsonLd={jobLd.length ? jobLd : undefined}
      />
      <PageMasthead
        dark
        kicker="Careers"
        titleLines={[{ text: "Build the future" }, { text: "of education", accent: false }, { text: "intelligence", accent: true }]}
        standfirst="We hire journalists, analysts and builders who care deeply about getting it right. Explore open roles across the Which Media group."
      />

      <section className="mx-auto max-w-[1400px] px-5 md:px-8 py-14 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {perks.map((p) => (
            <FadeUp key={p.title}>
              <div className="border-t-4 border-primary pt-6">
                <h3 className="headline text-2xl text-ink mb-3">{p.title}</h3>
                <p className="text-[15px] leading-relaxed text-foreground/70">{p.body}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-5 md:px-8 pb-24">
        <FadeUp>
          <h2 className="headline text-3xl md:text-5xl uppercase text-ink mb-8">
            {list.filter((j) => !j.closed).length} Open {list.filter((j) => !j.closed).length === 1 ? "Role" : "Roles"}
          </h2>
        </FadeUp>
        {list.length === 0 ? (
          <p className="text-xl text-foreground/60">
            No open roles right now — but we always want to hear from exceptional people.
          </p>
        ) : (
          <Accordion type="single" collapsible className="w-full" data-testid="jobs-accordion">
            {list.map((job) => (
              <AccordionItem key={job.id} value={job.id} className="border-foreground/15">
                <AccordionTrigger data-testid={`job-${job.id}`} className="py-6 hover:no-underline group text-left">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between w-full gap-2 pr-4">
                    <span className="headline text-xl md:text-3xl text-ink group-hover:text-primary transition-colors">
                      {job.title}
                      {job.closed && (
                        <span data-testid={`job-closed-${job.id}`} className="ml-3 align-middle inline-block font-sans text-[10px] font-extrabold uppercase tracking-widest bg-red-500/10 text-red-600 border border-red-500/30 rounded-full px-3 py-1">
                          Closed
                        </span>
                      )}
                    </span>
                    <div className="flex items-center gap-6 font-sans text-xs font-bold uppercase tracking-wide text-foreground/50">
                      <span className="flex items-center gap-2"><Briefcase size={14} /> {job.department}</span>
                      <span className="flex items-center gap-2"><MapPin size={14} /> {job.location}</span>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-8">
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    <RichText text={job.description} className="lg:col-span-8 text-lg leading-relaxed text-foreground/75" data-testid={`job-description-${job.id}`} />
                    <div className="lg:col-span-4 lg:text-right">
                      {job.closed ? (
                        <span data-testid={`job-closed-note-${job.id}`} className="inline-flex items-center gap-2 font-sans text-sm font-bold text-foreground/50 border-2 border-foreground/15 rounded-full px-6 py-3 cursor-not-allowed">
                          Applications closed
                        </span>
                      ) : (
                        <Link
                          to={`/contact?role=${encodeURIComponent(job.title)}`}
                          className="inline-flex items-center gap-2 font-sans text-sm font-bold bg-primary text-primary-foreground rounded-full pl-6 pr-5 py-3 hover:bg-ink transition-colors"
                        >
                          Apply — {job.type} <ArrowRight size={15} />
                        </Link>
                      )}
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </section>
    </div>
  );
};

export default Careers;
